import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { exportCSV } from '../utils/exportCSV';

function fmtTime(s) {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

export default function Attendance() {
  const { user } = useAuth();
  const [myStats, setMyStats] = useState(null);
  const [allRecords, setAllRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState('');

  useEffect(() => {
    api.get('/attendance/my').then(r => setMyStats(r.data));
    if (user?.role === 'superadmin') {
      api.get('/attendance/all').then(r => setAllRecords(r.data));
      api.get('/users').then(r => setUsers(r.data));
    }
  }, [user]);

  const fetchFiltered = () => {
    const q = filterUser ? `?userId=${filterUser}` : '';
    api.get(`/attendance/all${q}`).then(r => setAllRecords(r.data));
  };

  const reqSec = (myStats?.requiredHoursPerDay || 8) * 3600;
  const todayPct = myStats ? Math.min(100, Math.round((myStats.todaySeconds / reqSec) * 100)) : 0;

  return (
    <div className="fadein">
      <div className="page-header">
        <div>
          <div className="page-title">Attendance</div>
          <div className="page-subtitle">Work hours tracking</div>
        </div>
      </div>

      {/* My stats */}
      {myStats && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {/* Today card with progress */}
            <div className="stat-card">
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Today</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-1)', marginBottom: 10 }}>{fmtTime(myStats.todaySeconds)}</div>
              <div className="progress-bar" style={{ height: 5 }}>
                <div className="progress-fill" style={{ width: `${todayPct}%`, background: todayPct >= 100 ? 'var(--green)' : 'var(--accent)' }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 5 }}>
                {todayPct >= 100 ? 'Goal reached ✓' : `${todayPct}% of ${myStats.requiredHoursPerDay}h goal`}
              </div>
            </div>

            {/* Week */}
            <div className="stat-card">
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>This Week</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-1)' }}>{fmtTime(myStats.weekSeconds)}</div>
            </div>

            {/* Month */}
            <div className="stat-card">
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>This Month</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-1)' }}>{fmtTime(myStats.monthSeconds)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Admin records */}
      {user?.role === 'superadmin' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>All Records</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ width: 'auto', minWidth: 180 }}>
                <option value="">All Employees</option>
                {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
              </select>
              <button className="btn btn-secondary btn-sm" onClick={fetchFiltered}>Filter</button>
              <button className="btn btn-secondary btn-sm" onClick={() => {
                const rows = allRecords.map(r => ({
                  Employee: r.user?.name || '',
                  Role: r.user?.role || '',
                  Date: r.date || '',
                  'Total Time': fmtTime(r.totalSeconds),
                  Sessions: r.sessions?.length || 0,
                  Status: r.status || ''
                }));
                exportCSV('attendance.csv', rows, ['Employee', 'Role', 'Date', 'Total Time', 'Sessions', 'Status']);
              }}>Export CSV</button>
            </div>
          </div>
          <div className="card">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Role</th>
                  <th>Date</th>
                  <th>Total Time</th>
                  <th>Sessions</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {allRecords.map(r => (
                  <tr key={r._id}>
                    <td style={{ fontWeight: 500 }}>{r.user?.name}</td>
                    <td style={{ fontSize: 12.5, color: 'var(--text-3)', textTransform: 'capitalize' }}>{r.user?.role}</td>
                    <td style={{ color: 'var(--text-3)', fontSize: 12.5 }}>{r.date}</td>
                    <td style={{ fontWeight: 600 }}>{fmtTime(r.totalSeconds)}</td>
                    <td style={{ color: 'var(--text-3)', fontSize: 12.5 }}>{r.sessions?.length || 0}</td>
                    <td><span className={`badge badge-${r.status}`}>{r.status}</span></td>
                  </tr>
                ))}
                {allRecords.length === 0 && (
                  <tr><td colSpan={6}><div className="empty"><div className="empty-icon"></div><p>No records</p></div></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
